import React, { useState } from 'react';
import {
    ChartBarIcon,
    HeartIcon,
    StarIcon,
    BellIcon,
    UserIcon,
    ShoppingBagIcon,
    CameraIcon,
    PlayIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    XCircleIcon,
    ArrowTrendingUpIcon,
    CurrencyDollarIcon,
    EyeIcon,
    FireIcon,
    SparklesIcon,
    LightBulbIcon,
    RocketLaunchIcon,
    GlobeAltIcon,
    DevicePhoneMobileIcon,
    ComputerDesktopIcon,
    CloudIcon,
    ShieldCheckIcon,
    BoltIcon,
    MagnifyingGlassIcon,
    AdjustmentsHorizontalIcon,
    Bars3Icon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { Dialog } from '@headlessui/react';
import { HeartIcon as HeartSolidIcon, StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';

const UIShowcase = () => {
    const [activeTab, setActiveTab] = useState('hero');
    const [liked, setLiked] = useState(false);
    const [rating, setRating] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterOpen, setFilterOpen] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState([]);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [notifications, setNotifications] = useState([
        { id: 1, title: 'New Collection Available', message: 'Spring 2024 collection is now live', type: 'info', time: '2 min ago' },
        { id: 2, title: 'Order Completed', message: 'Your order #12345 has been processed', type: 'success', time: '1 hour ago' },
        { id: 3, title: 'Low Stock Alert', message: 'Item running low in inventory', type: 'warning', time: '3 hours ago' }
    ]);

    const tabs = [
        { id: 'hero', name: 'Hero Sections', icon: RocketLaunchIcon },
        { id: 'advanced', name: 'Advanced UI', icon: ChartBarIcon },
        { id: 'layouts', name: 'Layouts', icon: ShoppingBagIcon },
        { id: 'interactions', name: 'Interactions', icon: UserIcon },
        { id: 'data-viz', name: 'Data Visualization', icon: PlayIcon }
    ];

    const AlertComponent = ({ type, title, message, onClose }) => {
        const alertStyles = {
            success: 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200',
            warning: 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-200',
            error: 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200',
            info: 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200'
        };

        const icons = {
            success: CheckCircleIcon,
            warning: ExclamationTriangleIcon,
            error: XCircleIcon,
            info: InformationCircleIcon
        };

        const Icon = icons[type];

        return (
            <div className={`border rounded-lg p-4 flex items-start space-x-3 ${alertStyles[type]}`}>
                <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                    <h4 className="font-medium">{title}</h4>
                    <p className="text-sm mt-1">{message}</p>
                </div>
                {onClose && (
                    <button onClick={onClose} className="flex-shrink-0">
                        <XCircleIcon className="h-5 w-5" />
                    </button>
                )}
            </div>
        );
    };

    const ProductCard = ({ image, title, price, originalPrice, discount, rating }) => (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="relative">
                <img src={image} alt={title} className="w-full h-48 object-cover" />
                {discount && (
                    <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                        -{discount}%
                    </div>
                )}
                <button
                    onClick={() => setLiked(!liked)}
                    className="absolute top-2 right-2 p-2 rounded-full bg-white/80 hover:bg-white transition-colors"
                >
                    {liked ? (
                        <HeartSolidIcon className="h-5 w-5 text-red-500" />
                    ) : (
                        <HeartIcon className="h-5 w-5 text-gray-600" />
                    )}
                </button>
            </div>
            <div className="p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <div className="flex items-center space-x-2 mb-2">
                    <span className="text-lg font-bold text-gray-900 dark:text-white">${price}</span>
                    {originalPrice && (
                        <span className="text-sm text-gray-500 line-through">${originalPrice}</span>
                    )}
                </div>
                <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                        <StarSolidIcon
                            key={i}
                            className={`h-4 w-4 ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`}
                        />
                    ))}
                    <span className="text-sm text-gray-500 ml-2">({rating}/5)</span>
                </div>
            </div>
        </div>
    );

    const renderAdvanced = () => (
        <div className="space-y-12">
            {/* Advanced Dashboard Cards */}
            <div>
                <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Advanced Dashboard Components</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Revenue Chart Card */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 border border-gray-100 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Revenue Analytics</h4>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Monthly performance overview</p>
                            </div>
                            <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl">
                                <ArrowTrendingUpIcon className="h-6 w-6 text-white" />
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <span className="text-3xl font-bold text-gray-900 dark:text-white">$124,563</span>
                                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium dark:bg-green-900 dark:text-green-200">
                                    +12.5%
                                </span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700">
                                <div className="h-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full" style={{ width: '78%' }}></div>
                            </div>
                            <div className="grid grid-cols-3 gap-4 pt-4">
                                <div className="text-center">
                                    <div className="text-lg font-semibold text-gray-900 dark:text-white">$45K</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">This Week</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-lg font-semibold text-gray-900 dark:text-white">$89K</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">This Month</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-lg font-semibold text-gray-900 dark:text-white">$234K</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">This Year</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Activity Feed */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 border border-gray-100 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-6">
                            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h4>
                            <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">View All</button>
                        </div>
                        <div className="space-y-4">
                            {[
                                { icon: ShoppingBagIcon, color: 'bg-blue-500', title: 'New order received', time: '2 min ago', desc: 'Order #12345 from Sarah Johnson' },
                                { icon: UserIcon, color: 'bg-green-500', title: 'New user registered', time: '5 min ago', desc: 'Welcome to the platform!' },
                                { icon: CurrencyDollarIcon, color: 'bg-yellow-500', title: 'Payment processed', time: '10 min ago', desc: '$299.00 payment confirmed' },
                                { icon: BellIcon, color: 'bg-purple-500', title: 'System update', time: '1 hour ago', desc: 'New features available' }
                            ].map((activity, index) => (
                                <div key={index} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                    <div className={`p-2 rounded-lg ${activity.color}`}>
                                        <activity.icon className="h-4 w-4 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">{activity.title}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{activity.desc}</p>
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{activity.time}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Advanced Search & Filter */}
            <div>
                <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Advanced Search & Filter</h3>
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 border border-gray-100 dark:border-gray-700">
                    <div className="flex flex-col lg:flex-row gap-4 mb-6">
                        <div className="flex-1 relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search products, orders, customers..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            />
                        </div>
                        <button
                            onClick={() => setFilterOpen(!filterOpen)}
                            className="px-6 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-xl flex items-center space-x-2 transition-colors"
                        >
                            <AdjustmentsHorizontalIcon className="h-5 w-5" />
                            <span>Filters</span>
                        </button>
                    </div>

                    {filterOpen && (
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Category</label>
                                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                                        <option>All Categories</option>
                                        <option>Fashion</option>
                                        <option>Electronics</option>
                                        <option>Home & Garden</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Price Range</label>
                                    <div className="flex space-x-2">
                                        <input type="number" placeholder="Min" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                                        <input type="number" placeholder="Max" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                                    <div className="space-y-2">
                                        {['Active', 'Pending', 'Completed'].map((status) => (
                                            <label key={status} className="flex items-center">
                                                <input type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                                                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{status}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal Example */}
            <div>
                <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Modal & Overlay Components</h3>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all transform hover:scale-105 shadow-lg"
                >
                    Open Advanced Modal
                </button>
            </div>
        </div>
    );

    const renderCards = () => (
        <div className="space-y-8">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Product Cards</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <ProductCard
                    image="https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&h=300&fit=crop"
                    title="Luxury Watch"
                    price="599"
                    rating={5}
                />
                <ProductCard
                    image="https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&h=300&fit=crop"
                    title="Luxury Watch"
                    price="599"
                    rating={5}
                />
                <ProductCard
                    image="https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&h=300&fit=crop"
                    title="Running Shoes"
                    price="129"
                    originalPrice="179"
                    discount="28"
                    rating={4}
                />
            </div>

            {/* Stats Cards */}
            <div>
                <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Stats Cards</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
                        <div className="flex items-center">
                            <div className="p-2 bg-blue-100 rounded-lg dark:bg-blue-900">
                                <ChartBarIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Sales</p>
                                <p className="text-2xl font-semibold text-gray-900 dark:text-white">$12,426</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
                        <div className="flex items-center">
                            <div className="p-2 bg-green-100 rounded-lg dark:bg-green-900">
                                <UserIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">New Users</p>
                                <p className="text-2xl font-semibold text-gray-900 dark:text-white">1,426</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
                        <div className="flex items-center">
                            <div className="p-2 bg-purple-100 rounded-lg dark:bg-purple-900">
                                <ShoppingBagIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Orders</p>
                                <p className="text-2xl font-semibold text-gray-900 dark:text-white">426</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
                        <div className="flex items-center">
                            <div className="p-2 bg-yellow-100 rounded-lg dark:bg-yellow-900">
                                <StarIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Reviews</p>
                                <p className="text-2xl font-semibold text-gray-900 dark:text-white">4.8</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderForms = () => (
        <div className="space-y-8">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Form Components</h3>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg max-w-2xl">
                <form className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Full Name
                        </label>
                        <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="Enter your full name"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Email Address
                        </label>
                        <input
                            type="email"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="Enter your email"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Category
                        </label>
                        <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                            <option>Select a category</option>
                            <option>Fashion</option>
                            <option>Electronics</option>
                            <option>Home & Garden</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Message
                        </label>
                        <textarea
                            rows="4"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="Enter your message"
                        ></textarea>
                    </div>

                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                            I agree to the terms and conditions
                        </label>
                    </div>

                    <button
                        type="submit"
                        className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Submit Form
                    </button>
                </form>
            </div>
        </div>
    );

    const renderHeroSections = () => {
        // Define navigation items without using useState
        const navigation = [
            { name: 'Product', href: '#' },
            { name: 'Features', href: '#' },
            { name: 'Marketplace', href: '#' },
            { name: 'Company', href: '#' },
        ];

        return (
            <div className="space-y-8">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Hero Section Example</h3>

                <div className="bg-white rounded-xl overflow-hidden shadow-lg">
                    <header className="relative inset-x-0 top-0 z-50">
                        <nav aria-label="Global" className="flex items-center justify-between p-6 lg:px-8">
                            <div className="flex lg:flex-1">
                                <a href="#" className="-m-1.5 p-1.5">
                                    <span className="sr-only">Your Company</span>
                                    <img
                                        alt=""
                                        src="https://tailwindcss.com/plus-assets/img/logos/mark.svg?color=indigo&shade=600"
                                        className="h-8 w-auto"
                                    />
                                </a>
                            </div>
                            <div className="flex lg:hidden">
                                <button
                                    type="button"
                                    onClick={() => setMobileMenuOpen(true)}
                                    className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
                                >
                                    <span className="sr-only">Open main menu</span>
                                    <Bars3Icon aria-hidden="true" className="h-6 w-6" />
                                </button>
                            </div>
                            <div className="hidden lg:flex lg:gap-x-12">
                                {navigation.map((item) => (
                                    <a key={item.name} href={item.href} className="text-sm font-semibold leading-6 text-gray-900">
                                        {item.name}
                                    </a>
                                ))}
                            </div>
                            <div className="hidden lg:flex lg:flex-1 lg:justify-end">
                                <a href="#" className="text-sm font-semibold leading-6 text-gray-900">
                                    Log in <span aria-hidden="true">&rarr;</span>
                                </a>
                            </div>
                        </nav>
                        <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
                            <div className="fixed inset-0 z-50" />
                            <div className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white p-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
                                <div className="flex items-center justify-between">
                                    <a href="#" className="-m-1.5 p-1.5">
                                        <span className="sr-only">Your Company</span>
                                        <img
                                            alt=""
                                            src="https://tailwindcss.com/plus-assets/img/logos/mark.svg?color=indigo&shade=600"
                                            className="h-8 w-auto"
                                        />
                                    </a>
                                    <button
                                        type="button"
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="-m-2.5 rounded-md p-2.5 text-gray-700"
                                    >
                                        <span className="sr-only">Close menu</span>
                                        <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                                    </button>
                                </div>
                                <div className="mt-6 flow-root">
                                    <div className="-my-6 divide-y divide-gray-500/10">
                                        <div className="space-y-2 py-6">
                                            {navigation.map((item) => (
                                                <a
                                                    key={item.name}
                                                    href={item.href}
                                                    className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                                                >
                                                    {item.name}
                                                </a>
                                            ))}
                                        </div>
                                        <div className="py-6">
                                            <a
                                                href="#"
                                                className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                                            >
                                                Log in
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Dialog>
                    </header>

                    <div className="relative isolate px-6 pt-14 lg:px-8">
                        <div
                            aria-hidden="true"
                            className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
                        >
                            <div
                                style={{
                                    clipPath:
                                        'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
                                }}
                                className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
                            />
                        </div>
                        <div className="mx-auto max-w-2xl py-32 sm:py-48 lg:py-56">
                            <div className="hidden sm:mb-8 sm:flex sm:justify-center">
                                <div className="relative rounded-full px-3 py-1 text-sm leading-6 text-gray-600 ring-1 ring-gray-900/10 hover:ring-gray-900/20">
                                    Announcing our next round of funding.{' '}
                                    <a href="#" className="font-semibold text-indigo-600">
                                        <span aria-hidden="true" className="absolute inset-0" />
                                        Read more <span aria-hidden="true">&rarr;</span>
                                    </a>
                                </div>
                            </div>
                            <div className="text-center">
                                <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
                                    Data to enrich your online business
                                </h1>
                                <p className="mt-6 text-lg leading-8 text-gray-600">
                                    Anim aute id magna aliqua ad ad non deserunt sunt. Qui irure qui lorem cupidatat commodo. Elit sunt amet
                                    fugiat veniam occaecat.
                                </p>
                                <div className="mt-10 flex items-center justify-center gap-x-6">
                                    <a
                                        href="#"
                                        className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                    >
                                        Get started
                                    </a>
                                    <a href="#" className="text-sm font-semibold leading-6 text-gray-900">
                                        Learn more <span aria-hidden="true">→</span>
                                    </a>
                                </div>
                            </div>
                        </div>
                        <div
                            aria-hidden="true"
                            className="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]"
                        >
                            <div
                                style={{
                                    clipPath:
                                        'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
                                }}
                                className="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]"
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderAnimations = () => (
        <div className="space-y-8">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Animations & Interactions</h3>

            {/* Hover Effects */}
            <div>
                <h4 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">Hover Effects</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer">
                        <CameraIcon className="h-8 w-8 text-blue-600 mb-4" />
                        <h5 className="font-semibold text-gray-900 dark:text-white">Scale on Hover</h5>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">Hover to see the scale effect</p>
                    </div>

                    <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 rounded-xl shadow-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-300 cursor-pointer text-white">
                        <PlayIcon className="h-8 w-8 mb-4" />
                        <h5 className="font-semibold">Gradient Shift</h5>
                        <p className="text-blue-100 text-sm">Hover to see gradient change</p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:bg-blue-50 dark:hover:bg-gray-700 transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-blue-500">
                        <BellIcon className="h-8 w-8 text-green-600 mb-4" />
                        <h5 className="font-semibold text-gray-900 dark:text-white">Color Change</h5>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">Hover to see background change</p>
                    </div>
                </div>
            </div>

            {/* Loading States */}
            <div>
                <h4 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">Loading States</h4>
                <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="text-gray-700 dark:text-gray-300">Loading spinner</span>
                    </div>

                    <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                        <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
                    </div>

                    <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                </div>
            </div>

            {/* Interactive Rating */}
            <div>
                <h4 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">Interactive Rating</h4>
                <div className="flex items-center space-x-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                        <button
                            key={star}
                            onClick={() => setRating(star)}
                            className="transition-colors duration-200"
                        >
                            <StarSolidIcon
                                className={`h-8 w-8 ${star <= rating ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-200'
                                    }`}
                            />
                        </button>
                    ))}
                    <span className="ml-4 text-gray-700 dark:text-gray-300">
                        {rating > 0 ? `${rating}/5 stars` : 'Click to rate'}
                    </span>
                </div>
            </div>
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    UI Component Showcase
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                    Explore beautiful Tailwind CSS + React components to inspire your next project
                </p>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-gray-200 dark:border-gray-700 mb-8">
                <nav className="-mb-px flex space-x-8">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                    }`}
                            >
                                <Icon className="h-5 w-5" />
                                <span>{tab.name}</span>
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Tab Content */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-6">
                {activeTab === 'hero' && renderHeroSections()}
                {activeTab === 'advanced' && renderAdvanced()}
                {activeTab === 'layouts' && renderCards()}
                {activeTab === 'interactions' && renderAnimations()}
                {activeTab === 'data-viz' && renderForms()}
            </div>

            {/* Notifications Panel */}
            <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Notification Panel</h3>
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 max-w-md">
                    <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-gray-900 dark:text-white">Recent Notifications</h4>
                        <BellIcon className="h-5 w-5 text-gray-500" />
                    </div>
                    <div className="space-y-3">
                        {notifications.map((notification) => (
                            <div key={notification.id} className="flex items-start space-x-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
                                <div className={`w-2 h-2 rounded-full mt-2 ${notification.type === 'success' ? 'bg-green-500' :
                                    notification.type === 'warning' ? 'bg-yellow-500' :
                                        notification.type === 'error' ? 'bg-red-500' : 'bg-blue-500'
                                    }`}></div>
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">{notification.title}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{notification.message}</p>
                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{notification.time}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UIShowcase;